import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Calendar, MapPin, Medal } from 'lucide-react';
import { conquistasAPI } from '@/lib/api';

const conquistasHistoricas = [
  {
    titulo: 'Campeãs do 1º Campeonato Municipal de Voleibol Canaense',
    categoria: 'ACEC Feminino Adulto A',
    data: '17 de Maio de 2025',
    posicao: '1º Lugar',
    tipo: 'ouro'
  },
  {
    titulo: '3º Lugar do 1º Campeonato Municipal de Voleibol Canaense',
    categoria: 'ACEC Feminino Adulto B',
    data: '17 de Maio de 2025',
    posicao: '3º Lugar',
    tipo: 'bronze'
  },
  {
    titulo: 'Campeãs da Superliga Carajás',
    categoria: 'ACEC Feminino Adulto',
    data: '18 de Agosto de 2025',
    local: 'Carajás',
    posicao: '1º Lugar',
    tipo: 'ouro'
  },
  {
    titulo: 'Campeãs do Torneio Encontro da Amizade',
    categoria: 'ACEC Juvenil Avançado',
    data: '14 de Setembro de 2025',
    posicao: '1º Lugar',
    tipo: 'ouro'
  },
  {
    titulo: '1º Lugar do 1º Campeonato de Voleibol Mirim 4x4',
    categoria: 'ACEC Feminino I',
    data: '7 de Fevereiro de 2026',
    posicao: '1º Lugar',
    tipo: 'ouro'
  },
  {
    titulo: '3º Lugar do 1º Campeonato de Voleibol Mirim 4x4',
    categoria: 'IBESSPA I',
    data: '7 de Fevereiro de 2026',
    posicao: '3º Lugar',
    tipo: 'bronze'
  }
];

export default function Conquistas() {
  const [conquistas, setConquistas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConquistas = async () => {
      try {
        const res = await conquistasAPI.listar();
        setConquistas(res.data);
      } catch (error) {
        console.error('Erro ao carregar conquistas:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConquistas();
  }, []);

  const getMedalColor = (tipo) => {
    switch (tipo) {
      case 'ouro': return 'bg-yellow-400 text-yellow-900';
      case 'prata': return 'bg-slate-300 text-slate-800';
      case 'bronze': return 'bg-amber-600 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };

  const allConquistas = conquistas.length > 0 ? conquistas : conquistasHistoricas;

  return (
    <div data-testid="conquistas-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Trophy className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase">
              Nossas <span className="text-yellow-400">Conquistas</span>
            </h1>
            <p className="text-xl text-blue-100 mt-4 max-w-2xl mx-auto">
              Cada troféu representa dedicação, trabalho em equipe e superação
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="font-accent text-5xl sm:text-6xl font-bold text-blue-900">
                {allConquistas.filter(c => c.tipo === 'ouro' || c.posicao?.includes('1º')).length}
              </div>
              <div className="text-blue-800 font-bold uppercase tracking-wider text-sm">Ouros</div>
            </div>
            <div>
              <div className="font-accent text-5xl sm:text-6xl font-bold text-blue-900">
                {allConquistas.filter(c => c.tipo === 'prata' || c.posicao?.includes('2º')).length}
              </div>
              <div className="text-blue-800 font-bold uppercase tracking-wider text-sm">Pratas</div>
            </div>
            <div>
              <div className="font-accent text-5xl sm:text-6xl font-bold text-blue-900">
                {allConquistas.filter(c => c.tipo === 'bronze' || c.posicao?.includes('3º')).length}
              </div>
              <div className="text-blue-800 font-bold uppercase tracking-wider text-sm">Bronzes</div>
            </div>
          </div>
        </div>
      </section>

      {/* Lista de Conquistas */}
      <section className="py-24 bg-white" data-testid="conquistas-list">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="space-y-6">
              {allConquistas.map((conquista, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow flex flex-col sm:flex-row gap-6"
                  data-testid={`conquista-card-${index}`}
                >
                  {/* Medal */}
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full ${getMedalColor(conquista.tipo)} flex items-center justify-center flex-shrink-0`}>
                    <Medal className="w-8 h-8 sm:w-10 sm:h-10" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 uppercase mb-2">
                      {conquista.titulo}
                    </h3>
                    
                    {conquista.categoria && (
                      <p className="text-blue-700 font-medium mb-3">
                        {conquista.categoria}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      {conquista.data && (
                        <span className="flex items-center gap-1.5">
                          <Calendar size={16} />
                          {conquista.data}
                        </span>
                      )}
                      {conquista.local && (
                        <span className="flex items-center gap-1.5">
                          <MapPin size={16} />
                          {conquista.local}
                        </span>
                      )}
                    </div>

                    {conquista.posicao && (
                      <span className={`inline-block mt-3 px-4 py-1.5 rounded-full text-sm font-bold ${getMedalColor(conquista.tipo)}`}>
                        {conquista.posicao}
                      </span>
                    )}
                  </div>

                  {/* Image */}
                  {conquista.imagem_url && (
                    <div className="w-full sm:w-40 h-32 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={conquista.imagem_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${conquista.imagem_url}` : conquista.imagem_url}
                        alt={conquista.titulo}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
